<script setup>
// Preferences view — extracted from schedule-builder.vue (Task 6) so a second
// wizard can reuse it. Purely a dumb view: no fetch/save/composable calls.
// The parent owns loading, persistence, and the #actions buttons.
import { form_field_error_border_2026, form_error_text_2026 } from '~~/shared/utilities/ui';

const model = defineModel();
const props = defineProps({
    submitting: { type: Boolean, default: false },
    error: { type: String, default: '' },
    // Returning viewer — one who already has saved preferences, so the field
    // arrives pre-populated and the copy addresses editing rather than
    // introducing the builder. The parent decides; this stays a dumb view.
    returning: { type: Boolean, default: false },
});

// text-left: the field sits in a text-center column, which would otherwise centre
// both the placeholder and what the user types.
const textareaBase = 'w-full text-left bg-white border rounded-[10px] py-[25px] px-[26px] min-h-[180px] text-[15px] leading-[23px] tracking-[0.3px] font-hubspot-sans font-light text-(--magenta) placeholder:text-(--dark-pink) resize-y focus:outline-none';

// The stroke turns red while the error is up, matching the login form (Asana
// 687576). Swapped rather than appended: two competing border-colour utilities
// resolve by stylesheet order, not class order, so appending wouldn't reliably win.
const textareaCls = computed(() => [
    textareaBase,
    props.error ? form_field_error_border_2026 : 'border-(--dark-pink)',
]);
</script>

<template>
    <div class="flex flex-col items-center w-full max-w-[620px] text-center">
        <p v-if="returning" data-test="prefs-body-returning" class="font-hubspot-sans font-light text-[16px] leading-[26px] text-(--magenta) m-0">
            We’ll build out the rest of your schedule around any sessions or My Events that you’ve
            already added.
        </p>
        <p v-else data-test="prefs-body-new" class="font-hubspot-sans font-light text-[16px] leading-[26px] text-(--magenta) m-0">
            If you have already added any sessions or My Events to My Schedule, we'll make sure to
            build around those. We already know a bit about you from your registration but we can
            personalize your schedule even more with some additional information.
        </p>

        <!-- One prompt, not a new/returning pair (Asana 687564). The field is
             effectively never blank: with no saved preferences it still seeds an
             AI summary of the viewer's registration, so "Tell us about yourself"
             was addressing an empty state that doesn't happen — and it briefly
             showed the new-viewer wording before the seed landed and filled the
             box. Nick raised the change and cc'd Shannon, since it departs from
             the approved two-variant copy. -->
        <p data-test="prefs-prompt" class="font-hubspot-sans font-medium text-[16px] leading-[26px] text-(--magenta) mt-[16px] mb-[20px]">
            Please add to or edit your existing information, topics or HubSpot products of interest,
            and anything you’re hoping to takeaway from UNBOUND:
        </p>

        <textarea v-model="model" :class="textareaCls" placeholder="Example: I’m the Social Media Manager at a fintech startup. I’d like to learn more about marketing tech, data analytics, and AI. I’m especially looking for more info on HubSpot Marketing Hub and Breeze Agents, and would love to walk away with an actionable plan on how to transition our marketing team to being an AI first department." />

        <!-- Left-aligned with the field, which the centred column would otherwise
             pull to the middle (Asana 687576). -->
        <p v-if="error" data-test="prefs-error" :class="form_error_text_2026">{{ error }}</p>
    </div>
</template>
